import React, {useState, useMemo, useEffect, useCallback} from "react"
import {
    Modal,
    Select,
    Button,
    Input,
    notification,
    Checkbox
} from "antd";
import _ from "lodash";
import {useSelector, useDispatch} from "react-redux";
import {createMarkerExport} from "../redux/actions/index"
import { combineContinuousObservations } from "./util/PlaylistUtils";

const sessionIDSelector = state => _.get(state, ["playlist", "data", "routeID"]);
const userEmailSelector = state => state.userDetails.email;
const markerSelector = state => {
    const sessionID = _.get(state, ["playlist", "data", "routeID"])
    return _.get(state.markers.perSession, [sessionID], []);
}
const thresholdFilterSelector = (state) => state.markerThresholdFilters;
const sessionObservationsSelector = state => state.sessionObservations;
const detectionTypesSelector = state => state.detectionTypes;

const emptyObject = {};
const routeLocationSelector = state => state.playlist.data.route_locations;

const MarkerExportModal = ({closeModal, observationType=null}) => {

    const dispatch = useDispatch();

    const sessionID = useSelector(sessionIDSelector);
    const userEmail = useSelector(userEmailSelector)
    const markers = useSelector(markerSelector);
    const detectionTypes = useSelector(detectionTypesSelector);
    const thresholdFilters = useSelector(thresholdFilterSelector);
    const routeLocationData = useSelector(routeLocationSelector);
    const sessionObservations = useSelector(sessionObservationsSelector);

    const [exportMarkupTypes, setExportMarkupTypes] = useState(observationType ? `${observationType}_observations` : "all");
    const [reviewStatus, setReviewStatus] = useState(["all"]);

    const [recipient, setRecipient] = useState(userEmail);
    const [name, setName] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [allSessionsToggle, setAllSessionsToggle] = useState(false);
    const [allSessionsELR, setAllSessionsELR] = useState("");

    const currentRouteID = useSelector((state) => state.playlist.data.routeID);
    const defaultMarkers = useSelector((state) => state.defaultMarkersThresholds[currentRouteID] || emptyObject);


    const onClick = () => {
        let filters = {}
        if(reviewStatus && reviewStatus[0] !== "all"){
            filters.review_status_filters = reviewStatus;
        }
        if(exportMarkupTypes !== "all"){
            filters.types = exportMarkupTypes;
        }
        filters.score_filters = {}

        detectionTypes.filter(type => {
            return type.min_score || type.max_score
        }).forEach(type => {
            const defValue = _.get(defaultMarkers, [type.type], type.default_score)
            const filterValue = _.get(thresholdFilters, [type.type], defValue);
            filters.score_filters[type.type] = filterValue;
        })

        if(allSessionsToggle){
            filters.exportELR = allSessionsELR;
        }

        console.log("debug filters", filters)

        setExportLoading(true)
        dispatch(createMarkerExport(sessionID, name, recipient, filters))
            .then(success => {
                setExportLoading(false);
                if(success){
                    notification.success({
                        message: 'Successfully triggered export',
                    })
                    closeModal()
                }else{
                    notification.error({
                        message: 'Error creating export',
                    })
                }
            })
    }

    const combinedObservations = useMemo(() => {
        return combineContinuousObservations(sessionObservations);
    }, [sessionObservations])

    const onReviewStatusChange = useCallback((options) => {
        if((options.includes("all") && !reviewStatus.includes("all")) || options.length === 0){
            options = ["all"]
        }else if(reviewStatus.includes("all") && options.length){
            const index = options.indexOf("all");
            if (index > -1) {
                options.splice(index, 1);
            }
        }

        setReviewStatus(options)
    }, [reviewStatus])

    const exportCount = useMemo(() => {
        const filteredMarkers = markers.filter(marker => {
            if(exportMarkupTypes !== "all"){
                if(marker.name !== exportMarkupTypes){
                    return false
                }
            }
            if(reviewStatus && reviewStatus[0] !== "all"){
                if(reviewStatus.includes(marker.review_status)){
                    return false
                }
            }
            
            let detectionType = _.find(detectionTypes, function (ann) {
                return ann.type === marker.name;
            });

            if(!detectionType || detectionType.display_type === "never"){
                return false
            }
            console.log("debug detectionType", detectionType)

            const defValue = _.get(defaultMarkers, [detectionType.type], detectionType.default_score)
            console.log("debug def value", {defValue, thresholdFilters});
            

            if(!_.isNil(thresholdFilters[detectionType.type])){
                if(marker.score && marker.score < thresholdFilters[detectionType.type]){
                    return false
                }
            } else if (defValue){
                if (marker.score && marker.score < defValue){
                    return false
                }
            }
            return true

        });

        const filteredObservations = combinedObservations.filter(observation => {
            if(exportMarkupTypes !== "all"){
                if(`${observation.class}_observations` !== exportMarkupTypes){
                    return false
                }
            }

            if(reviewStatus && reviewStatus[0] !== "all"){
                if(!reviewStatus.includes(observation.review_status)){
                    return false
                }
            }

            return true;
        })
        return filteredMarkers.length + filteredObservations.length
    }, [markers, combinedObservations, exportMarkupTypes, reviewStatus, detectionTypes, defaultMarkers, thresholdFilters]);

    const markerTypes = useMemo(() => {
        const observationTypes = _.uniqBy(sessionObservations, "class");
        const markerTypes = _.map(_.uniqBy(markers, "name"), "name").filter(type => _.findIndex(observationTypes, {class: type}));

        if(!observationType){
            const markerOptions = markerTypes.map(type => {
                return <Select.Option label={type} value={type} key={type}>{type}</Select.Option>
            })
            return [<Select.Option label="all" value={"all"}>All</Select.Option>, ...markerOptions];
        }else{
            return observationTypes.filter(obsObj => {
                return obsObj.class === observationType;
            }).map(obsObj => {
                return <Select.Option
                          label={`${obsObj.display_name || obsObj.class} Observations`}
                          value={`${obsObj.class}_observations`}
                          key={`${obsObj.class}observation`}>
                            {obsObj.display_name || obsObj.class} Observations
                          </Select.Option>
            })
        }
    }, [markers, observationType, sessionObservations])

    const submitDisabled = useMemo(() => {
        return (
            name === "" ||
            recipient === "" ||
            (allSessionsToggle && !allSessionsELR)
        )
    }, [name, recipient, allSessionsToggle, allSessionsELR]);

    const elrOptions = useMemo(() => {
        
        return _.uniq(routeLocationData.map(locationData => {
            return _.get(locationData, ["location", 0], null)
        })).map(elr => {
            return <Select.Option value={elr} key={elr}>{elr}</Select.Option>
        })
    }, [routeLocationData])

    return (
        <Modal
            title="Detection Export"
            visible={true}
            dropdownMatchSelectWidth={false}
            onOk={closeModal}
            onCancel={closeModal}
            footer={[
                <Button onClick={closeModal}>Cancel</Button>,
                <Button loading={exportLoading} type="primary" onClick={onClick} disabled={submitDisabled}>Create Export</Button>
            ]}>
            <div className="MarkupExportBody">

                <h3>Export Name</h3>
                <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    style={{marginBottom: 10}}></Input>

                <h3>Email Recipient</h3>
                <Input
                    value={recipient}
                    onChange={e => setRecipient(e.target.value)}
                    style={{marginBottom: 30}}></Input>

                <div className={"exportMarkupsContainer"}>
                
                    <h3>Filters</h3>
                    <div className="exportInputDiv">
                        <h4 className="exportInputLabel">Detection Type</h4>
                        <Select
                            optionLabelProp="label"
                            value={exportMarkupTypes}
                            onChange={setExportMarkupTypes}
                            className="exportInput">
                            
                            {markerTypes}
                        </Select>
                    </div>

                    <div className="exportInputDiv">
                        <h4 className="exportInputLabel">Review status</h4>
                        <Select
                            value={reviewStatus}
                            onChange={onReviewStatusChange}
                            className="exportInput"
                            style={{width: 170}}
                            mode="multiple">
                            <Select.Option value={"all"} key="all">All</Select.Option>
                            <Select.Option value={1} key="verified">Verified</Select.Option>
                            <Select.Option value={2} key="verifiedandhidden">Verified & Hidden</Select.Option>
                            <Select.Option value={0} key="needs_review">Needs Review</Select.Option>
                            <Select.Option value={3} key="rejected">Rejected</Select.Option>
                        </Select>
                    </div>
                </div>


                {!observationType && <>
                    <h3>
                        Include Detections from other sessions
                        <Checkbox
                            disabled={exportMarkupTypes.includes("_observations")}
                            style={{marginLeft: 10}}
                            checked={allSessionsToggle}
                            onChange={e => setAllSessionsToggle(e.target.checked)}/>
                    </h3>

                    <div className={"exportInputDiv" + (allSessionsToggle ? "" : " Disabled")}>
                        <h4 className="exportInputLabel">ELR To Export</h4>
                        <Select
                            style={{width: 200}}
                            disabled={!allSessionsToggle}
                            value={allSessionsELR}
                            onChange={setAllSessionsELR}>
                            {elrOptions}
                        </Select>
                    </div>
                </>
                }

                {!allSessionsToggle && <p style={{marginBottom : -10, marginTop: 20}}>{exportCount} markers expected</p>}
            </div>
        </Modal>
    )
}

export default MarkerExportModal;
